import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Grid,
  Chip,
  Avatar,
  Stack,
  Button,
  alpha,
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  ViewList as ViewListIcon,
  Refresh as RefreshIcon,
  CalendarMonth as CalendarMonthIcon,
  Assessment as TotalIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { useAuth } from '../../../contexts/AuthContext';
import axiosInstance from '../../../utils/axios';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import FilterBar from '../../common/FilterBar';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const NEON = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';

const MotionCard = motion(Card);

const glassCardSx = {
  borderRadius: '14px',
  background: 'linear-gradient(160deg, rgba(5,13,26,0.97) 0%, rgba(10,22,40,0.95) 100%)',
  border: '1px solid rgba(56,189,248,0.16)',
  boxShadow: '0 12px 34px rgba(0,0,0,0.35), inset 0 1px 0 rgba(56,189,248,0.08)',
  backdropFilter: 'blur(16px)',
  position: 'relative',
  overflow: 'hidden',
};

const StatTile = ({ icon: Icon, title, value, subValue, color }) => (
  <Card sx={{ ...glassCardSx, flex: 1, minWidth: 190 }}>
    <CardContent sx={{ p: 2.2 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar sx={{ bgcolor: alpha(color, 0.22), color, width: 36, height: 36 }}>
          <Icon sx={{ fontSize: 20 }} />
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: 'rgba(226,232,240,0.95)', fontWeight: 700, lineHeight: 1.1 }}>
            {value}
          </Typography>
          <Typography sx={{ color: 'rgba(148,163,184,0.95)', fontSize: '0.78rem' }}>
            {title}
          </Typography>
          {subValue ? (
            <Typography sx={{ color: alpha(color, 0.95), fontSize: '0.75rem', mt: 0.25 }}>
              {subValue}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const MonthlyExpensesChart = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [budgetData, setBudgetData] = useState(null);
  const [viewMode, setViewMode] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
  });

  const filterOptions = useMemo(() => {
    const y = new Date().getFullYear();
    return [
      {
        name: 'year',
        label: 'Year',
        type: 'select',
        options: [
          { value: y, label: String(y) },
          { value: y - 1, label: String(y - 1) },
          { value: y - 2, label: String(y - 2) },
          { value: y - 3, label: String(y - 3) },
        ],
      },
    ];
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const currentYear = Number(filters.year) || new Date().getFullYear();
      const budgetsRes = await axiosInstance.get(`/budgets/monthly-totals?year=${currentYear}`);
      setBudgetData(budgetsRes.data);
    } catch (err) {
      console.error('Error fetching monthly budget data:', err);
      setError('Failed to fetch monthly budget data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.year]);

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <FeedbackMessage
          message="Please log in to view this page."
          severity="error"
          sx={{
            fontFamily: "'DM Sans',sans-serif",
            fontSize: '0.9rem',
            background: alpha('#450a0a', 0.92),
            border: '1px solid rgba(248,113,113,0.3)',
            color: '#fca5a5',
            boxShadow: '0 4px 20px rgba(239,68,68,0.12)',
          }}
        />
      </Container>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '75vh' }}>
        <Loading message="Loading monthly budget analysis" size={56} variant="primary" />
      </Box>
    );
  }

  const monthlyData = budgetData?.monthly_data || [];
  const yearlyData = budgetData?.yearly_summary || {};

  const filteredMonthlyData = monthlyData.filter((month) => {
    const matchesSearch = month.month_name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const chartData = {
    labels: filteredMonthlyData.map((item) => item.month_name),
    datasets: [
      {
        label: 'Budget',
        backgroundColor: alpha(NEON3, 0.72),
        borderColor: NEON3,
        borderWidth: 1.2,
        borderRadius: 6,
        data: filteredMonthlyData.map((item) => item.budget?.total || 0),
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: 'rgba(226,232,240,0.82)',
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 18,
        },
      },
      title: {
        display: true,
        text: `Monthly Budget Analysis (${filters.year})`,
        color: 'rgba(226,232,240,0.92)',
      },
      tooltip: {
        backgroundColor: 'rgba(5,13,26,0.95)',
        borderColor: 'rgba(56,189,248,0.35)',
        borderWidth: 1,
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: RM ${Number(ctx.parsed.y || 0).toLocaleString()}`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: 'rgba(148,163,184,0.9)',
          callback: (v) => `RM ${Number(v).toLocaleString()}`,
        },
        grid: { color: 'rgba(56,189,248,0.08)' },
      },
      x: {
        ticks: { color: 'rgba(148,163,184,0.9)' },
        grid: { display: false },
      },
    },
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>
      <Header
        title="Monthly Budget Analysis"
        subtitle="Track budget values across months"
        icon={BarChartIcon}
        actionButton={(
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            sx={{
              bgcolor: alpha(NEON, 0.22),
              border: `1px solid ${alpha(NEON, 0.45)}`,
              color: '#E2E8F0',
              '&:hover': { bgcolor: alpha(NEON, 0.35) },
            }}
          >
            Refresh
          </Button>
        )}
      />

      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError(null)}
        sx={{
          mb: 2.5,
          fontFamily: "'DM Sans',sans-serif",
          fontSize: '0.9rem',
          background: alpha('#450a0a', 0.92),
          border: '1px solid rgba(248,113,113,0.3)',
          color: '#fca5a5',
          boxShadow: '0 4px 20px rgba(239,68,68,0.12)',
        }}
      />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2.5 }}>
        <StatTile
          icon={TotalIcon}
          title="Total Budget"
          value={`RM ${Number(yearlyData.total_amount || 0).toLocaleString()}`}
          subValue={`${yearlyData.total_budgets || 0} entries`}
          color={NEON}
        />
      </Stack>

      <MotionCard
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28 }}
        elevation={0}
        sx={{ ...glassCardSx, mb: 2.5 }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: showFilters ? 2 : 0 }}>
            <Tabs value={viewMode} onChange={(_, v) => setViewMode(v)} sx={{ minHeight: 40 }}>
              <Tab icon={<BarChartIcon />} iconPosition="start" label="Chart" />
              <Tab icon={<ViewListIcon />} iconPosition="start" label="List" />
            </Tabs>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setShowFilters((v) => !v)}
              sx={{ borderColor: alpha(NEON2, 0.35), color: alpha(NEON2, 0.95) }}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
          </Box>

          {showFilters ? (
            <FilterBar
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters(!showFilters)}
              filters={filters}
              onFilterChange={setFilters}
              onClearFilters={() => {
                setFilters({ year: new Date().getFullYear() });
                setSearchTerm('');
              }}
              filterOptions={filterOptions}
              searchPlaceholder="Search months..."
            />
          ) : null}

          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              icon={<CalendarMonthIcon sx={{ fontSize: 15 }} />}
              label={`Year ${filters.year}`}
              variant="outlined"
              sx={{ color: 'rgba(226,232,240,0.85)', borderColor: alpha(NEON, 0.3) }}
            />
          </Stack>
        </CardContent>
      </MotionCard>

      {viewMode === 0 ? (
        <MotionCard
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, delay: 0.04 }}
          elevation={0}
          sx={glassCardSx}
        >
          <CardContent sx={{ p: 2.5 }}>
            {filteredMonthlyData.length > 0 ? (
              <Box sx={{ height: 430 }}>
                <Bar data={chartData} options={chartOptions} />
              </Box>
            ) : (
              <Box sx={{ minHeight: 260, display: 'grid', placeItems: 'center', textAlign: 'center' }}>
                <Box>
                  <BarChartIcon sx={{ fontSize: 44, color: alpha(NEON, 0.55) }} />
                  <Typography sx={{ color: 'rgba(226,232,240,0.9)', mt: 1, fontWeight: 600 }}>
                    No data for current filters
                  </Typography>
                </Box>
              </Box>
            )}
          </CardContent>
        </MotionCard>
      ) : (
        <Grid container spacing={2.2}>
          {filteredMonthlyData.map((month, index) => {
            const totalCount = month.budget?.count ?? month.total_count ?? 0;
            return (
              <Grid item xs={12} sm={6} lg={4} key={month.month}>
                <Card sx={glassCardSx}>
                  <CardContent sx={{ p: 2.4 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.2 }}>
                      <Stack direction="row" spacing={1.2} alignItems="center">
                        <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(NEON, 0.22), color: NEON }}>
                          <CalendarMonthIcon sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Box>
                          <Typography sx={{ color: 'rgba(226,232,240,0.95)', fontWeight: 700, lineHeight: 1.1 }}>
                            {month.month_name}
                          </Typography>
                          <Typography sx={{ color: 'rgba(148,163,184,0.95)', fontSize: '0.74rem' }}>
                            Month #{String(index + 1).padStart(2, '0')}
                          </Typography>
                        </Box>
                      </Stack>
                      <Typography sx={{ color: alpha(NEON3, 0.95), fontWeight: 700 }}>
                        RM {Number(month.total_amount || 0).toLocaleString()}
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        size="small"
                        label={`${month.budget?.count || 0} Budget items`}
                        sx={{ color: NEON3, borderColor: alpha(NEON3, 0.45) }}
                        variant="outlined"
                      />
                      <Chip size="small" label={`${totalCount} items`} sx={{ color: 'rgba(226,232,240,0.86)', borderColor: 'rgba(148,163,184,0.3)' }} variant="outlined" />
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Container>
  );
};

export default MonthlyExpensesChart;
